import axios, { AxiosInstance } from 'axios';

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    // Any successful write may have added or changed a vendor/unit/category, so drop the lookups cache.
    if (response.config.method && response.config.method.toLowerCase() !== 'get') clearLookupsCache();
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = async (username: string, password: string) => {
  const { data } = await api.post('/auth/login', { email: username, password });
  return data;
};

export const getMe = async () => {
  const { data } = await api.get('/auth/me');
  return data.user;
};

// Dashboard
export const getDashboard = async (params?: {
  departmentId?: number;
  departmentIds?: string;
  startDate?: string;
  endDate?: string;
}) => {
  const { data } = await api.get('/dashboard', { params });
  return data;
};

// Material Requests

/** Decimals arrive as strings over JSON; every money/qty field is read through Number(). */
export type Numeric = number | string;

/** Catalog link carried by MR lines and by the MR line behind a PO line. */
export interface ListCatalogItemRef {
  id: number;
  partNumber: string;
  description: string | null;
  descriptionAr: string | null;
}

/** Vendor as it comes back inside list rows (Prisma Vendor, so appCode/nameAr are present). */
export interface ListVendorRef {
  id: number;
  name: string;
  code?: string | null;
  appCode?: string | null;
  nameAr?: string | null;
  [key: string]: any;
}

export interface MRListLine {
  id: number;
  lineNo: number;
  partNumber: string | null;
  itemDescription: string;
  qty: Numeric;
  unitPrice: Numeric;
  lineTotal: Numeric;
  lineApprovalStatus: string;
  lineStatus: string;
  vendorSource: string | null;
  origUnitPrice: Numeric | null;
  refinedAt: string | null;
  catalogItem: ListCatalogItemRef | null;
  vendor: ListVendorRef | null;
  unit: { id: number; name: string } | null;
  currency: { id: number; code: string } | null;
  costCenter: any | null;
  poLines?: Array<{
    id: number;
    poHeaderId: number;
    poHeader: { id: number; poId: string; status: string } | null;
    qty: Numeric;
    unitPrice: Numeric;
    lineTotal: Numeric;
    actualUnitPrice: Numeric | null;
    actualLineTotal: Numeric | null;
  }>;
  [key: string]: any;
}

export interface MRListRow {
  id: number;
  mrId: string;
  department: { id: number; code: string; name: string } | null;
  requestedBy: { id: number; displayName: string } | null;
  requestDate: string;
  neededBy: string | null;
  createdAt: string;
  urgency: string;
  status: string;
  mrTotal: Numeric | null;
  actualTotal: Numeric | null;
  /** Net of cancelled (2026-09-22): requested − cancelled lines − parts cancelled on a PO as not needed. */
  netTotal?: number;
  cancelledTotal?: number;
  /** Kept share of the linked PO lines at actual price; null when no PO line has an actual yet. */
  netActualTotal?: number | null;
  firstPoId: string | null;
  lineCount: number;
  poCount: number;
  lines: MRListLine[];
  [key: string]: any;
}

/** Counts for the same department/date scope, ignoring the status filter. */
export interface MRListAttention {
  submitted: number;
  approvedNotOrdered: number;
  neededByPassed: number;
  /** MRs with a line cancellation waiting for the CEO (2026-09-15). */
  lineCancellations?: number;
}

/** Ask to cancel an approved, not-ordered MR line (requester of the MR, procurement, CEO). Returns the refreshed MR. */
export const requestMRLineCancel = async (mrId: number | string, lineId: number, reason: string) => {
  const { data } = await api.post(`/mrs/${mrId}/lines/${lineId}/cancel-request`, { reason });
  return data;
};

/** CEO / admin: approve or reject a pending request; approve with a reason and no request = direct cancel. Returns the refreshed MR. */
export const decideMRLineCancel = async (
  mrId: number | string,
  lineId: number,
  body: { decision: 'approve' | 'reject'; reason?: string },
) => {
  const { data } = await api.post(`/mrs/${mrId}/lines/${lineId}/cancel-decision`, body);
  return data;
};

export interface MRListResponse {
  data: MRListRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  statusCounts: Record<string, number>;
  attention: MRListAttention;
}

export const listMRs = async (params?: {
  departmentId?: number;
  departmentIds?: string;
  status?: string;
  statuses?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  lineCancellations?: string;
}): Promise<MRListResponse> => {
  const { data } = await api.get('/mrs', { params });
  return data;
};

export const getMR = async (mrId: number | string) => {
  const { data } = await api.get(`/mrs/${mrId}`);
  return data;
};

export const createMR = async (mrData: {
  departmentId: number;
  neededBy: string;
  urgency: string;
  notes?: string;
  lines: Array<{
    partNumber?: string;
    itemDescription: string;
    categoryId?: number;
    vendorId?: number;
    vendorName?: string;
    qty: number;
    unitId?: number;
    unitPrice: number;
    currencyId?: number;
    lineNotes?: string;
  }>;
}) => {
  const { data } = await api.post('/mrs', mrData);
  return data;
};

export const updateMR = async (mrId: string | number, mrData: Record<string, any>) => {
  const { data } = await api.put(`/mrs/${mrId}`, mrData);
  return data;
};

export const submitMR = async (mrId: number) => {
  const { data } = await api.post(`/mrs/${mrId}/submit`);
  return data;
};

/** Pull a Submitted MR back to Draft (requester only, while no line has been acted on). */
export const recallMR = async (mrId: number) => {
  const { data } = await api.post(`/mrs/${mrId}/recall`);
  return data;
};

export const approveLines = async (
  mrId: number,
  approvals: Array<{
    lineId: number;
    approved: boolean;
    rejectReason?: string;
  }>
) => {
  const { data } = await api.post(`/mrs/${mrId}/approve`, { approvals });
  return data;
};

export const getApprovableLines = async (departmentId?: number) => {
  const { data } = await api.get('/mrs/approvable', {
    params: departmentId ? { departmentId } : undefined,
  });
  return data;
};

// Purchase Orders

export interface POListLine {
  id: number;
  lineNo: number;
  partNumber: string | null;
  itemDescription: string;
  qty: Numeric;
  unitPrice: Numeric;
  lineTotal: Numeric;
  actualUnitPrice: Numeric | null;
  actualLineTotal: Numeric | null;
  totalReceived: Numeric;
  balanceQty: Numeric;
  unit: { name: string } | null;
  currency: { code: string } | null;
  costCenter: any | null;
  mrLine: {
    lineNo: number;
    catalogItem: ListCatalogItemRef | null;
    mrHeader: { mrId: string; department: { id: number; code: string; name: string } | null } | null;
  } | null;
  [key: string]: any;
}

export interface POListRow {
  id: number;
  poId: string;
  department: { id: number; code: string; name: string } | null;
  vendor: ListVendorRef | null;
  createdBy: { id: number; displayName: string } | null;
  poDate: string;
  createdAt: string;
  status: string;
  poTotal: Numeric | null;
  actualTotal: Numeric | null;
  receivedValue: Numeric;
  openValue: Numeric;
  /** Net of cancelled (2026-09-22): shown total − cancelled part; stored totals stay as ordered. */
  netValue?: number;
  cancelledValue?: number;
  netEstimate?: number;
  cancelledEstimate?: number;
  lineCount: number;
  firstMrId: string | null;
  lateFlag: boolean;
  overdueFlag: boolean;
  poAgeDays: number | null;
  lines: POListLine[];
  [key: string]: any;
}

/** Counts for the same department/date scope, ignoring the status filter. */
export interface POListAttention {
  overEstimate: number;
  late: number;
  openValue: number;
  /** POs with a line cancellation waiting for approval (2026-09-14). */
  lineCancellations?: number;
}

export interface POListResponse {
  data: POListRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  statusCounts: Record<string, number>;
  attention: POListAttention;
}

export const listPOs = async (params?: {
  departmentId?: number;
  departmentIds?: string;
  status?: string;
  statuses?: string;
  vendorId?: number;
  vendorIds?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}): Promise<POListResponse> => {
  const { data } = await api.get('/pos', { params });
  return data;
};

export const getPO = async (poId: number | string) => {
  const { data } = await api.get(`/pos/${poId}`);
  return data;
};

export const createPO = async (mrLineIds: number[], notes?: string, reviewed?: boolean) => {
  const { data } = await api.post('/pos', { mrLineIds, notes, reviewed });
  return data;
};

export const updatePOLines = async (
  poId: string,
  updates: Array<{ poLineId: number; partNumber?: string; actualUnitPrice?: number }>
) => {
  const { data } = await api.put(`/pos/${poId}/lines`, { updates });
  return data;
};

export const updatePOStatus = async (poId: number | string, status: string, reason?: string, mrAction?: 'reorder' | 'not_needed') => {
  const { data } = await api.put(`/pos/${poId}/status`, { status, reason, mrAction });
  return data;
};

export const receivePO = async (
  poId: number | string,
  items: Array<{ poLineId: number; receivedQty: number; notes?: string }>
) => {
  const { data } = await api.post(`/pos/${poId}/receive`, { items });
  return data;
};

// Lookups — cached per signed-in token for 5 minutes (2026-09-25): 31 screens called this on every mount,
// firing 5 requests each time. Cleared by any successful write (see the response interceptor).
const LOOKUPS_TTL_MS = 5 * 60 * 1000;
let lookupsCache: { token: string | null; at: number; promise: ReturnType<typeof fetchLookups> } | null = null;

export function clearLookupsCache() {
  lookupsCache = null;
}

export const getLookups = async () => {
  const token = localStorage.getItem('token');
  if (!lookupsCache || lookupsCache.token !== token || Date.now() - lookupsCache.at > LOOKUPS_TTL_MS) {
    const entry = { token, at: Date.now(), promise: fetchLookups() };
    lookupsCache = entry;
    entry.promise.catch(() => { if (lookupsCache === entry) lookupsCache = null; });
  }
  const l = await lookupsCache.promise;
  // Fresh arrays per caller so a screen that edits its copy cannot change what the next screen sees.
  return {
    departments: [...(l.departments || [])],
    vendors: [...(l.vendors || [])],
    categories: [...l.categories],
    categoryTree: [...l.categoryTree],
    units: [...(l.units || [])],
    currencies: [...(l.currencies || [])],
  };
};

const fetchLookups = async () => {
  const [departments, vendors, categories, units, currencies] =
    await Promise.all([
      api.get('/lookups/departments'),
      api.get('/lookups/vendors'),
      api.get('/lookups/categories'),
      api.get('/lookups/units'),
      api.get('/lookups/currencies'),
    ]);
  return {
    departments: departments.data,
    vendors: vendors.data,
    categories: Array.isArray(categories.data) ? categories.data : (categories.data?.flat || []),
    categoryTree: Array.isArray(categories.data) ? [] : (categories.data?.tree || []),
    units: units.data,
    currencies: currencies.data,
  };
};

// Admin - CRUD for lookups
export const createVendor = async (vendorData: { name: string; code?: string; contactInfo?: string }) => {
  const { data } = await api.post('/lookups/vendors', vendorData);
  return data;
};

export const createCategory = async (categoryData: { name: string; code?: string; parentId?: number; description?: string }) => {
  const { data } = await api.post('/lookups/categories', categoryData);
  return data;
};

export const createUnit = async (unitData: { name: string; abbreviation?: string }) => {
  const { data } = await api.post('/lookups/units', unitData);
  return data;
};

export const listUsers = async () => {
  const { data } = await api.get('/auth/users');
  return data;
};

export const createUser = async (userData: {
  email: string;
  password: string;
  displayName: string;
  roles: string[];
  departmentId: number;
  extraDepartmentIds?: number[];
}) => {
  const { data } = await api.post('/auth/register', userData);
  return data;
};

export const updateUser = async (userId: number, data: { displayName?: string; email?: string; role?: string; roles?: string[]; departmentId?: number; extraDepartmentIds?: number[]; isActive?: boolean; password?: string }) => {
  const { data: result } = await api.put(`/auth/users/${userId}`, data);
  return result;
};

export const deleteUser = async (userId: number, force?: boolean) => {
  const { data } = await api.delete(`/auth/users/${userId}`, { params: force ? { force: 'true' } : undefined });
  return data;
};

// Permissions
export const getMyPermissions = async (): Promise<Record<string, string>> => {
  const { data } = await api.get('/permissions/my');
  return data;
};

export const listAllPermissions = async (filters?: { grantType?: string; grantValue?: string }) => {
  const { data } = await api.get('/permissions', { params: filters });
  return data;
};

export const listResources = async () => {
  const { data } = await api.get('/permissions/resources');
  return data;
};

export const upsertPermission = async (perm: { resource: string; accessLevel: string; grantType: string; grantValue: string }) => {
  const { data } = await api.put('/permissions', perm);
  return data;
};

// Step 5f (item 17): Admin -> Permissions -> By Person.
export interface PersonAccess { user: { id: number; name: string; roles: string[]; department: { code: string; name: string } | null }; rows: Array<{ resource: string; fromRoleAndDepartment: string; given: string | null; gets: string }> }
export const getPersonAccess = async (userId: number) => (await api.get(`/permissions/person/${userId}`)).data as PersonAccess;
export const deletePermission = async (id: number) => {
  const { data } = await api.delete(`/permissions/${id}`);
  return data;
};

export const amendMRLine = async (mrId: number | string, lineId: number, updates: { qty?: number; rejectReason?: string; lineApprovalStatus?: string }) => {
  const { data } = await api.put(`/mrs/${mrId}/lines/${lineId}`, updates);
  return data;
};

export const updateVendor = async (vendorId: number, data: { name?: string; code?: string; contactInfo?: string }) => {
  const { data: result } = await api.put(`/lookups/vendors/${vendorId}`, data);
  return result;
};

export const deleteVendor = async (vendorId: number) => {
  const { data } = await api.delete(`/lookups/vendors/${vendorId}`);
  return data;
};

export const updateCategory = async (categoryId: number, data: { name?: string; code?: string; parentId?: number; description?: string; isActive?: boolean }) => {
  const { data: result } = await api.put(`/lookups/categories/${categoryId}`, data);
  return result;
};

export const deleteCategory = async (categoryId: number) => {
  const { data } = await api.delete(`/lookups/categories/${categoryId}`);
  return data;
};

export const updateUnit = async (unitId: number, data: { name?: string; abbreviation?: string }) => {
  const { data: result } = await api.put(`/lookups/units/${unitId}`, data);
  return result;
};

export const deleteUnit = async (unitId: number) => {
  const { data } = await api.delete(`/lookups/units/${unitId}`);
  return data;
};

export const deleteMR = async (mrId: number | string) => {
  const { data } = await api.delete(`/mrs/${mrId}`);
  return data;
};

export const deleteMRLine = async (mrId: number | string, lineId: number) => {
  const { data } = await api.delete(`/mrs/${mrId}/lines/${lineId}`);
  return data;
};

export type POLineMrAction = 'reorder' | 'not_needed';

// Cancellations queue (2026-09-16): one read model over whole-PO, PO-line and MR-line requests.
export interface CancellationRow {
  key: string;
  type: 'po' | 'po_line' | 'mr_line';
  status: 'Pending' | 'Approved' | 'Rejected';
  document: string;
  documentId: number;
  documentPath: string;
  lineId?: number;
  lineNo?: number;
  item: { code: string | null; description: string; descriptionAr: string | null };
  qty: number;
  ofQty: number;
  unit: string | null;
  received: number;
  value: number;
  department: { id: number; code: string; name: string } | null;
  vendor: { id: number; name: string } | null;
  requestedBy: string | null;
  requestedById: number | null;
  requestedAt: string | null;
  ageHours: number | null;
  reason: string | null;
  mrAction: POLineMrAction | null;
  mrRef: string | null;
  decidedBy?: string | null;
  decidedAt?: string | null;
  decisionNote?: string | null; // 2026-10-24: why a PO line request was not approved
  children?: CancellationRow[];
}
export interface CancellationQueueResult {
  status: 'pending' | 'decided';
  summary: { count: number; lines: number; value: number; oldestHours: number; byType: { po: number; po_line: number; mr_line: number } };
  rows: CancellationRow[];
  truncated: boolean;
  canDecide: boolean;
}
export const listCancellations = async (params?: { status?: 'pending' | 'decided'; type?: CancellationRow['type']; limit?: number; days?: number; departmentIds?: string }) => {
  const { data } = await api.get('/cancellations', { params });
  return data as CancellationQueueResult;
};

/** Ask to cancel part or all of a PO line's open balance; the CEO decides (2026-09-14). Returns the refreshed PO. */
export const requestPOLineCancel = async (
  poId: number | string,
  lineId: number,
  body: { qty: number; reason: string; mrAction: POLineMrAction },
) => {
  const { data } = await api.post(`/pos/${poId}/lines/${lineId}/cancel-request`, body);
  return data;
};

/** CEO / admin decision on a pending line cancellation. Returns the refreshed PO. */
export const decidePOLineCancel = async (
  poId: number | string,
  lineId: number,
  body: { decision: 'approve' | 'reject'; mrAction?: POLineMrAction; note?: string }, // note: why not approved (2026-10-24)
) => {
  const { data } = await api.post(`/pos/${poId}/lines/${lineId}/cancel-decision`, body);
  return data;
};

export const deletePOLine = async (poId: number | string, lineId: number) => {
  const { data } = await api.delete(`/pos/${poId}/lines/${lineId}`);
  return data;
};

export const bulkUpdatePOStatus = async (
  poIds: number[],
  status: string,
  reason?: string
) => {
  const { data } = await api.put('/pos/bulk-status', { poIds, status, reason });
  return data;
};

export const deletePO = async (poId: number | string) => {
  const { data } = await api.delete(`/pos/${poId}`);
  return data;
};

// Goods Receiving Notes (GRN)
export const listGRNs = async (params?: {
  poId?: string;
  vendorId?: number;
  search?: string;
  vendorIds?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}) => {
  const { data } = await api.get('/grns', { params });
  return data;
};

export const getGRN = async (grnId: string) => {
  const { data } = await api.get(`/grns/${grnId}`);
  return data;
};

export const createGRN = async (payload: {
  poId: string | number;
  deliveryNoteRef?: string;
  supplierInvoice?: string;
  receivedOn?: string;
  notes?: string;
  lines: Array<{
    poLineId: number;
    deliveredQty: number;
    acceptedQty: number;
    rejectedQty?: number;
    inspectionNotes?: string;
    rejectReason?: string;
    notes?: string;
  }>;
}) => {
  const { data } = await api.post('/grns', payload);
  return data;
};

// ── GRN phase 1 (2026-10-21): two purchase paths and the new receive window (/api/receiving) ──────────
export type PaymentMethod = 'cash' | 'card' | 'transfer' | 'on_account';
// Step 4a (2026-10-25): the door outcomes are replacement, short_close and discount; 'return' and 'writeoff' stay for receipts saved before.
export type RejectOutcome = 'return' | 'replacement' | 'discount' | 'writeoff' | 'short_close';
export interface ProofFile { key: string; name: string; size: number; mime: string }
export interface LeadTime { vendorId: number; vendor: string; days: number; basis: 'history' | 'vendor' | 'default'; orders: number }
export interface ReceiveLinePayload {
  poLineId: number;
  arrivedQty: number;
  rejectedQty?: number;
  rejectReason?: string;
  actualUnitPrice?: number;
  costCenterId?: number | null;
  outcome?: { type: RejectOutcome; dueDate?: string; amount?: number; alreadyPaid?: boolean; note?: string; mrAction?: 'reorder' | 'not_needed' };
  rest?: { action: 'coming' | 'close'; date?: string; reason?: string; mrAction?: 'reorder' | 'not_needed' };
  replacementAsked?: boolean;        // door 2 (step 2): the window asked whether this delivery is the replacement
  replacesGrnLineId?: number | null; // … and it is, for that rejected line
}
export interface ReceivePayload {
  receivedOn: string;
  receivedOnReason?: string;
  deliveryNoteRef?: string;
  supplierInvoice?: string;
  proofFiles?: ProofFile[];
  notes?: string;
  lines: ReceiveLinePayload[];
}
export interface DirectPurchasePayload {
  boughtOn: string;
  boughtOnReason?: string;
  paymentMethod: PaymentMethod;
  proofFiles?: ProofFile[];
  noProofReason?: string;
  notes?: string;
  lines: Array<{ mrLineId: number; qty: number; unitPrice: number; costCenterId?: number | null }>;
}
export interface ExpectedDeliveryRow {
  id: number; poId: string; status: string;
  vendor: { id: number; name: string }; mrIds: string[];
  expected: string | null; lateDays: number; quietDays: number | null; partly: boolean; replacement: boolean;
  openValue: number;
  openLines: Array<{ id: number; lineNo: number; item: string; code: string | null; open: number; qty: number; unit: string | null; pendingCancel: boolean }>;
  state: 'stale' | 'late' | 'due_today' | 'this_week' | 'later';
}
export interface ExpectedDeliveries {
  today: string;
  counts: {
    dueToday: number; dueTodayValue: number; late: number; lateOldestDays: number; thisWeek: number; stale: number; staleValue: number;
    replacements: number; rejections: number; rejectionsValue: number; // 2026-10-22: "Rejections to resolve" tile
    rejectionReceipts?: number; // 2026-10-24: on how many receipts (what the Receipts filter shows)
  };
  rows: ExpectedDeliveryRow[];
}
export const uploadReceiptProof = async (files: File[]) => {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  return (await api.post('/receiving/proof', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data as ProofFile[];
};
export const getReceiptProofLink = async (grnId: string, index: number) =>
  (await api.get(`/receiving/proof/${grnId}/${index}`)).data.url as string;
export const getVendorLeadTime = async (vendorId: number) => (await api.get(`/receiving/lead-time/${vendorId}`)).data as LeadTime;
export const createVendorOrder = async (mrLineIds: number[], expectedDelivery: string, notes?: string) =>
  (await api.post('/receiving/order', { mrLineIds, expectedDelivery, notes })).data as any[];
export const createDirectPurchase = async (payload: DirectPurchasePayload) =>
  (await api.post('/receiving/direct', payload)).data as { po: any; grn: any; warnings: string[] };
export const receiveGoods = async (poId: string, payload: ReceivePayload) =>
  (await api.post(`/receiving/${poId}/receive`, payload)).data as { grn: any; warnings: string[] };
export const getExpectedDeliveries = async () => (await api.get('/receiving/expected')).data as ExpectedDeliveries;
// Goods Receiving → Receipts, per the mockup (2026-10-22): path, value, Goes to; filters with counts.
export interface ReceiptRow {
  id: number; grnId: string; receivedOn: string; createdAt: string; receivedBy: { id: number; displayName: string } | null;
  po: { id: number; poId: string } | null; vendor: { id: number; name: string; appCode?: string | null } | null;
  department: { id: number; code: string; name: string } | null;
  path: 'direct' | 'order' | null; paymentMethod: string | null; newWindow: boolean;
  value: number; units: Array<{ unit: string; qty: number }>; goesTo: Array<{ code: string; name: string | null }>;
  rejected: number; toResolve: boolean; proofCount: number; noProofReason: string | null;
  deliveryNoteRef: string | null; supplierInvoice: string | null;
}
export interface ReceiptList {
  data: ReceiptRow[]; total: number; page: number; limit: number; totalPages: number;
  counts: { all: number; direct: number; order: number; rejections: number; noProof: number };
}
export const listReceipts = async (params: {
  page?: number; limit?: number; search?: string; vendorIds?: string; startDate?: string; endDate?: string;
  path?: 'direct' | 'order'; attention?: 'rejections' | 'noproof';
}) => (await api.get('/receiving/receipts', { params })).data as ReceiptList;

// Rejected goods step 1 (2026-10-24, mockup AUMPidbNn2tJqJ4ujXtcPv): where each rejected line of a receipt stands.
export type CaseState =
  | 'not_created' | 'to_ship' | 'waiting' | 'credited' | 'refunded' | 'replaced'
  | 'due' | 'late' | 'received' | 'closed'
  | 'waiting_credit' | 'pay_less' | 'settled'
  | 'not_sent' | 'waiting_ahmed' | 'approved' | 'not_approved'
  | 'closed_at_door' | 'not_closed'; // short close (step 4a)
export interface ReceiptCase {
  grnLineId: number; lineNo: number; unit: string | null;
  outcome: RejectOutcome | null; state: CaseState | null;
  qty: number; price: number; value: number; owed: number | null;
  alreadyPaid: boolean | null; // discount: paid already (vendor owes us) or not paid yet (pay less)
  reason: string | null; note: string | null; due: string | null; amount: number | null; ref: string | null;
  returns: Array<{ id: number; code: string | null; qty: number; value: number | null; status: string; creditNoteRef: string | null; refundRef: string | null; refundAmount: number | null; carrierName: string | null; trackingNumber: string | null; date: string; shipMethod?: string | null; shippedOn?: string | null; shipReceivedBy?: string | null }>;
  settlement?: { how: string; on: string | null; amount: number | null; reference: string | null; refundMethod: string | null; replacedBy: string | null } | null;
  replacedBy: Array<{ grnId: string; qty: number; receivedOn: string }>;
  decision: { status: 'Approved' | 'Rejected'; at: string | null; by: string | null; note?: string | null } | null;
  failures: Array<{ step: 'return' | 'request' | 'short_close'; qty: number; error: string; at: string }>;
  closeRestNotSent: boolean;
  // Step 4a: who short-closed it and when; whether the money the vendor owes for it is settled; later returns of the accepted goods.
  closedBy?: { name: string | null; at: string | null } | null;
  owedSettled?: boolean | null;
  laterReturns?: Array<{ id: number; code: string | null; qty: number; value: number | null; status: string; date: string }>;
  canChooseAnother: boolean;
  history: Array<{ outcome: string; note: string | null; decided?: string; decidedAt?: string | null; changedAt: string }>;
}
export interface ReceiptCases { grnId: string; newWindow: boolean; newReceivingSince: string | null; vendor: string | null; cases: ReceiptCase[] }
export const getReceiptCases = async (grnId: string) => (await api.get(`/receiving/receipts/${grnId}/cases`)).data as ReceiptCases;
export const retryReceiptStep = async (grnLineId: number, step: 'return' | 'request' | 'short_close') =>
  (await api.post(`/receiving/grn-lines/${grnLineId}/retry`, { step })).data as { ok: boolean; ref: string | null };
export const chooseAnotherOutcome = async (grnLineId: number, body: { type: 'replacement' | 'short_close'; dueDate?: string; note?: string; mrAction?: 'reorder' | 'not_needed' }) =>
  (await api.post(`/receiving/grn-lines/${grnLineId}/outcome`, body)).data as { ok: boolean; warnings: string[]; cases: ReceiptCases };

// Rejected goods step 2 (2026-10-25, mockup AUMPidbNn2tJqJ4ujXtcPv): Returns & credits, one Ship and one Settle, PO block, dashboard.
export type CreditCaseState = 'to_ship' | 'waiting' | 'waiting_credit' | 'pay_less' | 'settled';
export type SettleHow = 'credit_note' | 'refund' | 'other_invoice' | 'replaced' | 'paid_less';
export type CreditKind = 'return' | 'discount' | 'short_close' | 'invoice' | 'overpaid'; // invoice (step 4e): the vendor's mistake on an invoice it was paid more for; id = the invoice difference, code = the invoice number. overpaid (step 4j): money the vendor owes on a PO with no case of its own; id = the PO
export interface CreditCase {
  key: string; kind: CreditKind; id: number; code: string | null;
  source?: 'accepted' | 'door' | 'old' | null; // a return: later return of accepted goods (step 4a), at the door, or before the new receiving
  vendor: { id: number; name: string } | null;
  grn: { id: number; grnId: string; newWindow: boolean } | null; lineNo: number | null;
  po: { id: number; poId: string; status: string } | null; poLineId: number | null; poLineOpen: number;
  item: { id: number | null; code: string | null; description: string };
  qty: number; unit: string | null; reason: string | null; owed: number; old: boolean;
  since: string | null; ageDays: number; state: CreditCaseState; status: string;
  ship: { method: string | null; on: string | null; receivedBy: string | null; carrier: string | null; tracking: string | null; note: string | null } | null;
  settlement: { how: SettleHow; on: string | null; amount: number | null; reference: string | null; refundMethod: string | null; otherPo: string | null; otherInvoice: string | null; replacedBy: string | null; closedQty: number | null; legacy: boolean; payment?: string | null } | null;
  statusLabel: string; statusSub: string | null;
}
export interface CreditCaseList {
  newReceivingSince: string | null; view: string;
  tiles: {
    toShip: { count: number; value: number };
    waiting: { count: number; value: number; old: number };
    over14: { count: number; oldestDays: number };
    settledThisMonth: { count: number; value: number; byHow: Array<{ how: SettleHow; count: number }> };
  };
  counts: Record<'open' | 'to_ship' | 'waiting' | 'pay_less' | 'settled', number>;
  rows: CreditCase[];
}
export interface CreditsSummary {
  owed: { value: number; count: number; old: number };
  payLess: { value: number; count: number };
  settledThisMonth: { value: number; count: number; byHow: Array<{ how: SettleHow; count: number }> };
  over14: { count: number; oldestDays: number; oldestVendor: string | null };
  byVendor: Array<{ vendorId: number; name: string; openCases: number; owed: number; oldestDays: number; old: number }>;
}
export interface PoCredits {
  poId: string; cases: CreditCase[];
  totals: { cameBack: number; paidLess: number; stillOwed: number; toPayLess: number; closedQty: number };
}
export interface SettleInfo { case: CreditCase; wouldClose: number; deliveries: Array<{ grnHeaderId: number; grnId: string; receivedOn: string; accepted: number }> }
export interface SettlePayload {
  how: SettleHow; date: string; amount?: number; reference?: string; refundMethod?: 'cash' | 'instapay' | 'transfer';
  otherPoId?: string; otherInvoice?: string; replacedByGrnId?: number; note?: string;
}
export const listCreditCases = async (params: { view?: string; search?: string; vendorId?: number }) =>
  (await api.get('/returns-credits', { params })).data as CreditCaseList;
export const getCreditsSummary = async () => (await api.get('/returns-credits/summary')).data as CreditsSummary;
export const getPoCredits = async (poId: string | number) => (await api.get(`/returns-credits/po/${poId}`)).data as PoCredits;
export const getSettleInfo = async (kind: CreditKind, id: number) => (await api.get(`/returns-credits/${kind}/${id}/settle-info`)).data as SettleInfo;
export const shipReturn = async (returnId: number, body: { method: 'in_person' | 'courier' | 'collected'; date: string; receivedBy?: string; carrier?: string; tracking?: string; note?: string }) =>
  (await api.post(`/returns-credits/return/${returnId}/ship`, body)).data as CreditCase;
export const settleCase = async (kind: CreditKind, id: number, body: SettlePayload) =>
  (await api.post(`/returns-credits/${kind}/${id}/settle`, body)).data as CreditCase;
// Step 4a (2026-10-25, mockup DyrAbPJDgtF8t6mjFfytkv "Door & returns" 3 and 5): a later return of accepted goods, from the receipt page or
// "New return" on Returns & credits. Valued at the price paid; the PO and the receipt do not change.
export const LATER_RETURN_REASONS = [
  { value: 'damaged', label: 'Damaged or broken' },
  { value: 'quality', label: 'Quality problem' },
  { value: 'wrong_item', label: 'Wrong item' },
  { value: 'excess', label: 'More than needed' },
  { value: 'other', label: 'Other' },
] as const;
export interface ReturnableLine {
  grnLineId: number; grnId: string; receivedOn: string; poId: string | null; lineNo: number | null;
  vendor: { id: number; name: string } | null;
  item: { id: number | null; code: string | null; description: string }; unit: string | null;
  accepted: number; returned: number; canReturn: number; price: number;
  costCenter: { id: number; code: string; name: string } | null;
}
export const getReturnableLines = async (params: { vendorId?: number; search?: string; grnId?: string }) =>
  (await api.get('/returns-credits/returnable', { params })).data as ReturnableLine[];
export const createLaterReturn = async (body: { grnLineId: number; qty: number; reason: string; note?: string; photos?: ProofFile[] }) =>
  (await api.post('/returns-credits/returns', body)).data as CreditCase;
export interface OpenReplacement { grnLineId: number; poLineId: number; grnId: string; qty: number; remaining: number; due: string | null }
export const getOpenReplacements = async (poId: string) => (await api.get(`/receiving/${poId}/replacements`)).data as OpenReplacement[];

// Rejected goods step 3 (2026-10-25, mockup "Payments & ADIB"): M1 payments per PO, M2 the ADIB purchasing account.
export type PayMethod = 'instapay' | 'card_online' | 'card_pos' | 'cash' | 'company_bank' | 'safe' | 'vendor_credit' | 'own_money';
export interface PaymentRow {
  id: number; code: string; vendorId: number | null; paidOn: string; method: PayMethod; methodLabel: string; amount: number; onThisPo: number | null;
  pos: string[]; lines: Array<{ poHeaderId: number; poId: string | null; poLineId: number | null; amount: number }>;
  reference: string | null; payeeName: string | null; proofFiles: ProofFile[]; note: string | null; status: string; supersededBy: number | null;
  createdBy: string | null; createdAt: string;
  bank: { state: 'matched' | 'waiting' | 'none'; on: string | null; difference: number | null; reason: string | null };
  // Step 4c (M3): a purchase on the vendor's credit — what finance has paid of it, and by which FIN- payments.
  credit?: { covered: number; outstanding: number; by: Array<{ code: string; on: string | null; amount: number }> } | null;
  noPaper?: string | null; // step 5d (item 10): cash with no paper, why
  paidCurrency?: string; foreignAmount?: number | null; rate?: number | null; // step 5e (item 12)
}
export interface ClosedPayment {
  id: number; code: string; paidOn: string; methodLabel: string; amount: number; onThisPo: number | null; proofFiles: ProofFile[]; reference: string | null;
  status: 'superseded' | 'cancelled'; replacedBy: string | null; why: string | null; by: string | null; at: string | null;
}
export interface HandedWeek { statementId: number; from: string; to: string; handedTo: string; handedAt: string }
/** What Correct and Cancel need: the whole payment on all its POs, and what limits changing it (Ahmed 1 Nov). */
export interface PaymentDetail {
  payment: PaymentRow;
  rows: Array<{ poLineId: number; poHeaderId: number; poId: string; lineNo: number; item: string; unit: string | null; qty: number; received: number; value: number; paidBefore: number; owed: number; amount: number }>;
  charges: Array<{ poHeaderId: number; poId: string; total: number; amount: number; open: number }>;
  discounts: Array<{ grnLineId: number; amount: number; poId: string | null; lineNo: number | null; item: string | null }>;
  bankLine: { id: number; on: string; debit: number; narrative: string; frozen: HandedWeek | null } | null;
  handedWeeks: HandedWeek[];
}
export interface PoPayments {
  receiptPhotos?: Array<ProofFile & { grnId: string }>; // a direct purchase not paid yet: its receipt photo, offered as the payment's proof (Ahmed 1 Nov)
  po: { id: number; poId: string; status: string; vendor: { id: number; name: string } | null; purchasePath: string | null; paymentMethod: string | null; currency?: string };
  lines: Array<{ id: number; lineNo: number; item: string; code: string | null; unit: string | null; qty: number; received: number; cancelled: number; price: number; value: number; paid: number; owed: number }>;
  payments: PaymentRow[];
  closed: ClosedPayment[]; // entries that no longer count: replaced by a correction, or cancelled (Ahmed 1 Nov)
  credits: CreditCase[];
  payLessOpen: Array<{ grnLineId: number; lineNo: number | null; item: string; amount: number }>;
  takenOff?: Array<{ id: number; on: string | null; amount: number; reference: string | null; fromPo: string | null; what: string }>; // step 4j: what other cases of this vendor took off this PO
  payees: Array<{ kind: string; name: string }>;
  // toPay: still to pay on the lines that owe; vendorOwes: what lines paid for more than the goods kept are worth (step 4a, never netted).
  totals: { linesValue: number; returnedValue: number; discountTotal: number; paid: number; cameBack: number; paidLess: number; stillOwed: number; toPay: number; vendorOwes: number; invoiced: number | null; charges?: number; chargesOpen?: number; takenOff?: number; offLines?: number };
  status: 'not_paid' | 'part_paid' | 'paid' | 'overpaid' | 'before_system';
  bankConfirmed: boolean;
  // Step 5b (item 8): dated before the go-live date, paid outside the ERP; or on the opening list, with what was paid before go-live.
  beforeSystem?: boolean;
  opening?: OpeningPaid | null;
  goLiveOn?: string | null;
  neverInvoice?: NeverInvoice | null; // step 5d (item 11)
}
export interface VendorPo { id: number; poId: string; status: string; poDate: string; value: number; paid: number; toPay: number; offLines: number; lines: Array<{ id: number; lineNo: number; item: string; unit: string | null; qty: number; received: number; value: number; paid: number; owed: number }> }
/** Step 4j: the server's question when a payment would leave the vendor owing us more (409, confirm: 'overpay'); `overpayOk` is the answer. */
export interface OverpayLine { poId: string; vendor: string | null; toPay: number; paying: number; more: number; lastPayment: string | null; where: Array<{ label: string; toPay: number; paying: number }> }
export interface PaymentPayload {
  method: PayMethod; paidOn: string; amount: number; reference?: string; payeeName?: string; proofFiles?: ProofFile[]; note?: string;
  lines: Array<{ poLineId: number; amount: number }>; payLess?: number[];
  charges?: Array<{ poHeaderId: number; amount: number }>; // step 4d: a delivery or other charge recorded on the PO
  bankLineId?: number; // A7: recorded from this ADIB line, so no proof is asked
  overpayOk?: boolean; // step 4j: it pays more than is owed, and that is meant
  noPaperReason?: string; // step 5d (item 10): cash with no paper at all, why
  paidCurrency?: string; foreignAmount?: number; // step 5e (item 12): a USD PO paid in USD, or in EGP covering foreignAmount USD
  useCredits?: Array<{ key: string; amount: number }>; // step 5e (item 14): money the vendor holds, used on this payment
}
export const getPoPayments = async (poId: string | number) => (await api.get(`/payments/po/${poId}`)).data as PoPayments;
// Step 5c (item 15): what we owe each vendor.
export interface VendorBalances {
  goLive: { state: string; on: string } | null;
  totals: { toPay: number; paidAhead: { value: number; count: number }; hold: number; credit: number; usd: { value: number; count: number } };
  rows: Array<{ vendorId: number; name: string; posOpen: number; poIds: number[]; toPay: number; paidAhead: number; hold: number; credit: number; oldest: { on: string; days: number } | null }>;
}
export const getVendorBalances = async () => (await api.get('/payments/vendor-balances')).data as VendorBalances;
export const searchPosForPayment = async (q: string) => (await api.get('/payments/po-search', { params: { q } })).data as Array<{ id: number; poId: string; status: string; total: number; vendor: { id: number; name: string } | null }>;
export const getVendorPos = async (vendorId: number, except?: number) => (await api.get(`/payments/vendor/${vendorId}/pos`, { params: { except } })).data as VendorPo[];
// Step 5d (items 10, 11): paid with no paper; never has an invoice.
export interface NeverInvoice { by: 'po' | 'vendor'; reason: string; setBy: string | null; setAt: string | null }
export interface NoPaperRow { id: number; code: string; paidOn: string; amount: number; methodLabel: string; why: string; vendor: string | null; pos: string[] }
export const getNoPaper = async () => (await api.get('/payments/no-paper')).data as NoPaperRow[];
export const markNoPaperSeen = async (id: number) => (await api.post(`/payments/${id}/no-paper-seen`)).data;
export const getVendorNeverInvoice = async (vendorId: number) => (await api.get(`/payments/vendor/${vendorId}/never-invoice`)).data as { on: boolean; reason: string | null; setBy: string | null; setAt: string | null };
export const setVendorNeverInvoice = async (vendorId: number, on: boolean, reason?: string) => (await api.put(`/payments/vendor/${vendorId}/never-invoice`, { on, reason })).data as { on: boolean; reason: string | null; setBy: string | null; setAt: string | null };
export const setPoNeverInvoice = async (poId: string, reason: string) => (await api.post(`/payments/po/${poId}/never-invoice`, { reason })).data as NeverInvoice | null;
export const undoPoNeverInvoice = async (poId: string) => (await api.post(`/payments/po/${poId}/never-invoice/undo`)).data as NeverInvoice | null;
export const recordPayment = async (body: PaymentPayload) => (await api.post('/payments', body)).data as { id: number | null; code: string | null; used?: number; warning?: string };
// Step 5e (item 14): money a vendor holds of ours.
export interface VendorHold { key: string; kind: string; id: number; code: string | null; owed: number; po: string | null; since: string | null; what: string }
export const getVendorHolds = async (vendorId: number) => (await api.get(`/payments/vendor/${vendorId}/holds`)).data as VendorHold[];
export const correctPayment = async (id: number, body: Partial<PaymentPayload> & { why: string }) => (await api.post(`/payments/${id}/correct`, body)).data as { id: number; code: string };
export const getPaymentDetail = async (id: number) => (await api.get(`/payments/${id}`)).data as PaymentDetail;
export const cancelPayment = async (id: number, why: string) => (await api.post(`/payments/${id}/cancel`, { why })).data as { id: number; code: string; discountsReopened: number; bankLineReopened: boolean };
export const uploadPaymentProof = async (files: File[]) => {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  return (await api.post('/payments/proof', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data as ProofFile[];
};
export const getPaymentProofLink = async (paymentId: number, index: number) => (await api.get(`/payments/${paymentId}/proof/${index}`)).data.url as string;

export interface AccountSettings { procurementCanLoad: boolean; account: string; myAccess: string; canLoad: boolean; cardHolder?: string }
export interface BankRow {
  id: number; seq: number; date: string; narrative: string; debit: number; credit: number; balance: number | null;
  kind: string; resolution: string | null; state: 'matched' | 'needs' | 'outside' | 'fee' | 'other' | 'before';
  payment: { id: number; code: string; amount: number; paidOn: string; method: string; methodLabel: string; vendor: { id: number; name: string } | null; payeeName: string | null; pos: string[] } | null;
  matchedBy: string | null; difference: number | null; diffReason: string | null; diffNote: string | null;
  expenseKind: string | null; expenseLabel: string | null; costCenter: { id: number; code: string; name: string } | null; note: string | null; proofFiles: ProofFile[];
  refund: { id: number; amount: number; reference: string | null } | null; payeeText: string | null;
  // part (B3, 2026-10-26): the line is only part of a bigger payment to the same name (the rest went in another transfer).
  suggestions: Array<{ id: number; code: string; amount: number; paidOn: string; methodLabel: string; vendor: { id: number; name: string } | null; pos: string[]; difference: number; part?: boolean }>;
  // Step 5f (item 20): money in that looks like a transfer coming back.
  cameBack?: { paymentId: number; code: string; amount: number; sent: number; sentOn: string; payee: string | null; pos: string[]; chargeKept: number; sameName: boolean } | null;
  returned?: string | null;
}
export interface StatementView {
  statement: {
    id: number; from: string; to: string; opening: number; closing: number; totalDebit: number; totalCredit: number; lineCount: number;
    fileName: string | null; hasPdf: boolean; loadedAt: string; proven: boolean;
    previous: { from: string; to: string; closing: number; follows: boolean | null; gap: boolean } | null;
    handedIn: { to: string; at: string } | null;
  };
  tiles: { topUps: { value: number; count: number }; matched: { count: number; value: number; instapay: number; card: number }; fees: { value: number; count: number }; atm: { value: number; count: number }; needs: { count: number } };
  counts: { matched: number; needs: number; outside: number; fee: number; other: number; before?: number; missing: number };
  rows: BankRow[];
  missing: Array<{ id: number; code: string; amount: number; paidOn: string; methodLabel: string; vendor: { id: number; name: string } | null; pos: string[]; why: string }>;
  cashPayments: number;
  /** Step 5g: after loading, what the export added (lines already loaded are skipped). */
  loaded?: { added: number; repeated: number; older: number; from: string; to: string };
}
export interface StatementListRow { id: number; from: string; to: string; opening: number; closing: number; lineCount: number; fileName: string | null; hasPdf: boolean; loadedAt: string; loadedBy: string | null; toExplain: number; handedIn: { to: string; at: string } | null }
export interface SettlementFigures {
  statementId: number; from: string; to: string; proven: boolean; frozen: boolean;
  adib: {
    opening: number; topUps: { value: number; count: number }; refunds: { value: number; count: number }; repayments: { value: number; count: number }; profit: number;
    paidOnPos: { value: number; count: number; byMethod: Array<{ method: string; count: number; value: number }> }; fees: number;
    atm: { value: number; count: number }; outside: { value: number; count: number; byKind: Array<{ kind: string; value: number }> };
    personal: { value: number; count: number }; toExplain: { value: number; count: number }; closing: number;
  };
  cash: { start: number; atm: number; paid: number; moves: number; end: number };
  forYou: {
    noPo: Array<{ id: number; date: string; narrative: string; amount: number; note: string | null }>;
    outsideByKind: Array<{ kind: string; value: number }>; owedBack: number;
    somethingElse: Array<{ id: number; date: string; narrative: string; amount: number | null; note: string | null; vendor: string | null }>;
    vendorCredit: number; financePaid: number;
    vendorCreditByVendor?: Array<{ vendorId: number; name: string; value: number }>; // step 4c: bought on credit this week, per vendor
  };
  handedIn: { to: string; at: string; note?: string | null; by?: string | null } | null;
  cardHolder?: string;
}
export interface CashLedger {
  rows: Array<{ date: string; what: string; note: string | null; amount: number; ref: string; balance: number | null; owed?: 'owed' | 'repaid' | null }>; holdsNow: number; hasOpening: boolean;
  officer?: { owed: number; payments: number; repaid: number }; // step 5e (item 16): we owe the officer
}
export interface AccountTile { statementId: number; from: string; to: string; toExplain: number; handedIn: { to: string; at: string } | null; onCredit?: number }
const PA = '/purchasing-account';
export const getAccountSettings = async () => (await api.get(`${PA}/settings`)).data as AccountSettings;
export const setProcurementCanLoad = async (on: boolean) => (await api.put(`${PA}/settings`, { procurementCanLoad: on })).data as AccountSettings;
export const setCardHolder = async (name: string) => (await api.put(`${PA}/settings`, { cardHolder: name })).data as AccountSettings;
export const listStatements = async () => (await api.get(`${PA}/statements`)).data as StatementListRow[];
export const getStatement = async (id?: number) => (await api.get(id ? `${PA}/statements/${id}` : `${PA}/statements/latest`)).data as StatementView | null;
export const getStatementFile = async (id: number, which: 'csv' | 'pdf') => (await api.get(`${PA}/statements/${id}/file`, { params: { which } })).data.url as string;
export const loadStatement = async (file: File, pdf?: File | null) => {
  const fd = new FormData();
  fd.append('file', file);
  if (pdf) fd.append('pdf', pdf);
  return (await api.post(`${PA}/statements`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data as StatementView;
};
export const matchBankLine = async (lineId: number, paymentId: number) => (await api.post(`${PA}/lines/${lineId}/match`, { paymentId })).data as { ok: boolean; difference: number; charge?: boolean };
export const bankLineCameBack = async (lineId: number, paymentId: number) => (await api.post(`${PA}/lines/${lineId}/came-back`, { paymentId })).data;
export const unmatchBankLine = async (lineId: number) => (await api.post(`${PA}/lines/${lineId}/unmatch`)).data;
export const resolveBankLine = async (lineId: number, body: { as: string; expenseKind?: string; costCenterId?: number | null; note?: string; proofFiles?: ProofFile[]; settlementId?: number }) =>
  (await api.post(`${PA}/lines/${lineId}/resolve`, body)).data;
export const explainBankDifference = async (lineId: number, body: { reason: 'fee' | 'other_po' | 'corrected' | 'other' | 'split'; note?: string; addPoId?: number; addLines?: Array<{ poLineId: number; amount: number }>; lines?: Array<{ poLineId: number; amount: number }>; otherLineId?: number }) =>
  (await api.post(`${PA}/lines/${lineId}/difference`, body)).data;
// B3 (2026-10-26): "Paid in more than one transfer" — this line's part of the payment, the rest, and the bank lines that can be it.
export interface SplitCandidates {
  payment: { id: number; code: string; amount: number }; part: number; partCharge: number; rest: number;
  lines: Array<{ id: number; seq: number; statementId: number; date: string; narrative: string; debit: number; fit: 'exact' | 'charge' | 'differs'; difference: number }>;
}
export const getSplitCandidates = async (lineId: number) => (await api.get(`${PA}/lines/${lineId}/split-candidates`)).data as SplitCandidates;
// B1 (2026-10-26): ADIB's InstaPay charge inside the transfer — 0.1%, at least 0.50, at most 20.00 (the server books it as a fee).
export const instapayCharge = (amount: number) => Math.round(Math.min(20, Math.max(0.5, amount * 0.001)) * 100 + 1e-9) / 100;
/** The transfer inside a bank amount that includes the InstaPay charge: an exact fit, else a piaster either way (same rule as the server). */
export function withoutInstapayCharge(debit: number): number | null {
  const tries: number[] = [];
  for (const c of [debit - 20, debit - 0.5, debit / 1.001]) for (const d of [0, -0.01, 0.01]) { const x = Math.round((c + d) * 100) / 100; if (x > 0) tries.push(x); }
  for (const tol of [0.005, 0.011]) {
    const x = tries.find((t) => Math.abs(Math.round((t + instapayCharge(t)) * 100) / 100 - debit) <= tol);
    if (x != null) return x;
  }
  return null;
}
export const getSettlement = async (statementId: number) => (await api.get(`${PA}/settlements/${statementId}`)).data as SettlementFigures;
export const handInSettlement = async (statementId: number, body: { handedTo: string; handedToUserId?: number | null; note?: string }) => (await api.post(`${PA}/settlements/${statementId}/hand-in`, body)).data as SettlementFigures;
export const getWalidCash = async () => (await api.get(`${PA}/cash`)).data as CashLedger;
export const addCashMove = async (body: { date: string; kind: string; amount: number; note?: string }) => (await api.post(`${PA}/cash`, body)).data as CashLedger;
// ── Step 5b (item 8): the go-live date and the opening list ──
export interface OpeningPaid { answer: 'paid' | 'part' | 'not_paid'; amount: number; currency: string; on: string; tickedBy: string | null; approvedBy: string | null }
export interface GoLiveCounts { total: number; ticked: number; paid: number; part: number; notPaid: number; toPay: Record<string, number>; nothing: number; partReceived: number; recent: number; hand: number }
export interface GoLiveView {
  state: 'none' | 'draft' | 'sent' | 'approved'; goLiveOn: string | null; ceo: boolean; canSetDate: boolean; canTick: boolean; canApprove: boolean;
  set?: { by: string | null; at: string }; sent?: { by: string | null; at: string } | null; sentBack?: { by: string | null; at: string; note: string | null } | null;
  approved?: { by: string | null; at: string } | null; counts: GoLiveCounts | null; before: number | null;
}
export interface OpeningRow {
  id: number; poId: string; vendor: { id: number; name: string } | null; poDate: string | null; status: string; why: 'nothing' | 'part' | 'recent' | 'hand'; whyLabel: string;
  received: { kind: 'nothing' | 'part' | 'all'; text: string } | null; value: number; currency: string; answer: 'paid' | 'part' | 'not_paid' | null; paid: number | null; toPay: number | null;
  tickedBy: string | null; tickedAt: string | null;
}
export const getGoLive = async () => (await api.get(`${PA}/go-live`)).data as GoLiveView;
export const setGoLiveDate = async (date: string) => (await api.put(`${PA}/go-live/date`, { date })).data as GoLiveView;
export const getOpeningList = async () => (await api.get(`${PA}/go-live/list`)).data as { goLiveOn: string | null; state: GoLiveView['state']; rows: OpeningRow[] };
export const tickOpening = async (poHeaderId: number, answer: string, amount?: number | null) => (await api.put(`${PA}/go-live/list/${poHeaderId}`, { answer, amount })).data as OpeningRow;
export const searchOpeningAdd = async (q: string) => (await api.get(`${PA}/go-live/search`, { params: { q } })).data as Array<{ id: number; poId: string; poDate: string | null; status: string; vendor: string | null; firstItem: string | null }>;
export const addOpening = async (poHeaderId: number) => (await api.post(`${PA}/go-live/list`, { poHeaderId })).data as OpeningRow;
export const sendOpeningList = async () => (await api.post(`${PA}/go-live/send`)).data as GoLiveView;
export const sendBackOpeningList = async (note: string) => (await api.post(`${PA}/go-live/send-back`, { note })).data as GoLiveView;
export const addOpeningAfter = async (poHeaderId: number, answer: string, amount?: number | null) => (await api.post(`${PA}/go-live/after`, { poHeaderId, answer, amount })).data as { rows: OpeningRow[] };
export const approveOpeningList = async () => (await api.post(`${PA}/go-live/approve`)).data as GoLiveView;
// ── Step 5f (item 20): the audit page ──
export interface AuditFlag { key: string; kind: 'money' | 'paper' | 'pattern' | 'cash'; title: string; refs: string[]; reason: string; mark: { state: 'seen' | 'explained' | 'fine'; note: string | null; at: string; by: string | null } | null }
export interface AuditView {
  week: { statementId: number; from: string; to: string };
  reading: { id: number; status: 'running' | 'done' | 'failed'; trigger: string; model: string; counts: any; error: string | null; startedAt: string; finishedAt: string | null; by: string | null } | null;
  flags: AuditFlag[]; fine: Array<{ ref: string; note: string }>;
}
export const getAuditWeeks = async () => (await api.get(`${PA}/audit/weeks`)).data as Array<{ id: number; from: string; to: string; status: string | null }>;
export const getAudit = async (statementId: number) => (await api.get(`${PA}/audit/${statementId}`)).data as AuditView;
export const runAudit = async (statementId: number) => (await api.post(`${PA}/audit/${statementId}/run`)).data as AuditView;
export const markAuditFlag = async (statementId: number, key: string, state: string, note?: string) => (await api.post(`${PA}/audit/${statementId}/flags/${key}`, { state, note })).data as AuditView;
export const getAccountTile = async () => (await api.get(`${PA}/tile`)).data as AccountTile | null;
export const getOpenRefunds = async () => (await api.get(`${PA}/refunds`)).data as Array<{ id: number; on: string; amount: number; reference: string | null }>;

export const createGRNReturn = async (grnLineId: number, payload: {
  returnQty: number;
  returnReason: string;
  notes?: string;
}) => {
  const { data } = await api.post(`/grns/lines/${grnLineId}/returns`, payload);
  return data;
};

export const updateGRNReturnStatus = async (returnId: number, payload: Record<string, any>) => {
  const { data } = await api.patch(`/grns/returns/${returnId}`, payload);
  return data;
};

export const deleteGRN = async (grnId: string) => {
  const { data } = await api.delete(`/grns/${grnId}`);
  return data;
};

// Parts Catalog
export const catalogList = async (params?: Record<string, any>) => {
  const { data } = await api.get('/catalog', { params });
  return data as { items: CatalogItem[]; total: number; page: number; pageSize: number };
};

export const catalogGet = async (id: number) => {
  const { data } = await api.get(`/catalog/${id}`);
  return data as CatalogItemDetail;
};

export const catalogCreate = async (payload: CatalogCreatePayload) => {
  const { data } = await api.post('/catalog', payload);
  return data as CatalogItem;
};

export const catalogUpdate = async (id: number, payload: CatalogUpdatePayload) => {
  const { data } = await api.patch(`/catalog/${id}`, payload);
  return data as CatalogItem;
};

export const catalogDelete = async (id: number) => {
  const { data } = await api.delete(`/catalog/${id}`);
  return data as CatalogItem;
};

export const catalogSearch = async (q: string, limit?: number) => {
  const { data } = await api.get('/catalog/search', { params: { q, limit } });
  return data as CatalogSearchResult[];
};

export const catalogBulkCreate = async (items: Array<{
  partNumber: string;
  description: string;
  defaultUnitPrice?: number;
  unitName?: string;
  categoryName?: string;
  vendorName?: string;
  currencyCode?: string;
  sku?: string;
  barcode?: string;
}>) => {
  const { data } = await api.post('/catalog/bulk', { items });
  return data as { created: CatalogItem[]; errors: Array<{ row: number; partNumber: string; error: string }>; totalProcessed: number };
};

// Catalog browser (MR create modal): paged, normalised search (Arabic letter shapes, digits, hyphens ignored).
export interface CatalogBrowseResult {
  items: CatalogSearchResult[];
  total: number;
  page: number;
  pageSize: number;
}
export const catalogBrowse = async (params: { q?: string; class?: string; page?: number; pageSize?: number }) => {
  const { data } = await api.get('/catalog/browse', { params });
  return data as CatalogBrowseResult;
};

// Admin: department settings (requesters may enter an estimated unit price)
export const updateDepartmentSettings = async (id: number, payload: { requesterEntersPrice?: boolean }) => {
  const { data } = await api.patch(`/lookups/departments/${id}`, payload);
  return data;
};

// Price engine (2026-09-06)
export interface ItemVendorPrice { vendorId: number; vendorName: string; appCode: string | null; preferred: boolean; listPrice: number | null; listDate: string | null; currency: string | null; leadTimeDays: number | null; vendorPartNo: string | null; vendorColour: string | null; lastPaid: number | null; lastPo: string | null; lastPoDate: string | null; driftPct: number | null; current: number | null }
export interface ItemPrices { itemId: number; partNumber: string; unit: { id: number; name: string } | null; current: { price: number | null; vendorId: number | null; vendorName: string | null; rule: string; date: string | null }; vendors: ItemVendorPrice[]; timeline: Array<{ date: string; source: string; price: number; vendor: string; ref: string | null; by: string | null }>; altUnits: Array<{ id: number; name: string; factor: number }>; deptPreferences: DeptPreference[] }
export interface DeptPreference { departmentId: number; departmentCode: string; departmentName: string; vendorId: number; vendorName: string; appCode: string | null; reason: string | null; setBy: string | null; setAt: string }
export const catalogDeptVendors = (id: number) => api.get(`/catalog/${id}/dept-vendors`).then((r) => r.data as DeptPreference[]);
export const setCatalogDeptVendor = (id: number, departmentId: number, payload: { vendorId: number; reason?: string }) => api.put(`/catalog/${id}/dept-vendors/${departmentId}`, payload).then((r) => r.data as DeptPreference[]);
export const removeCatalogDeptVendor = (id: number, departmentId: number) => api.delete(`/catalog/${id}/dept-vendors/${departmentId}`).then((r) => r.data as DeptPreference[]);
export const catalogItemPrices = (id: number) => api.get(`/catalog/${id}/prices`).then((r) => r.data as ItemPrices);
export const catalogPricesBatch = (ids: number[]) => api.get('/catalog/prices', { params: { ids: ids.join(',') } }).then((r) => r.data as Record<number, ItemPrices>);

// Item Card (2026-09-09): everything connected to one catalog item in one call.
export interface CatalogCardRequest { mrLineId: number; mrHeaderId: number; mrId: string; lineNo: number; departmentId: number | null; department: string | null; requester: string | null; requestDate: string | null; neededBy: string | null; urgency: string | null; mrStatus: string | null; qty: number | null; unit: string | null; unitPrice: number | null; currency: string | null; lineStatus: string | null; approvalStatus: string | null; vendorId: number | null; vendor: string | null; vendorSource: string | null; costCenterCode: string | null; costCenterName: string | null; lineNotes: string | null; poLineId: number | null; poHeaderId: number | null; poId: string | null }
export interface CatalogCardOrder { poLineId: number; poHeaderId: number; poId: string; poDate: string | null; status: string | null; vendorId: number | null; vendor: string | null; appCode: string | null; qty: number | null; unitPrice: number | null; actualUnitPrice: number | null; pricePaid: number | null; lineTotal: number | null; actualLineTotal: number | null; currency: string | null; totalReceived: number; returnedQty: number; mrHeaderId: number; mrId: string; department: string | null; createdBy: string | null; costCenterCode: string | null; open: boolean }
export interface CatalogCardReturn { id: number; qty: number | null; date: string | null; reason: string | null; status: string | null; creditNoteRef: string | null; refundAmount: number | null; returnedBy: string | null }
export interface CatalogCardReceipt { grnLineId: number; grnHeaderId: number; grnId: string; receivedOn: string | null; poHeaderId: number; poId: string; vendor: string | null; deliveredQty: number; acceptedQty: number; rejectedQty: number; inspectionStatus: string | null; rejectReason: string | null; receivedBy: string | null; deliveryNoteRef: string | null; supplierInvoice: string | null; returns: CatalogCardReturn[] }
export interface CatalogCardVendor { vendorId: number; name: string; appCode: string | null; preferred: boolean; poCount: number; orderedQty: number; receivedQty: number; returnedQty: number; acceptedQty: number; rejectedQty: number; grnPassed: number; grnFailed: number; spendAccepted: number | null; avgPrice: number | null; firstPo: string | null; firstPoId: string; lastPo: string | null; lastPoId: string; openLines: number }
export interface CatalogCardCostCenter { code: string | null; name: string | null; lines: number; qty: number; mrCount: number; value: number | null }
export interface CatalogCard {
  item: { id: number; partNumber: string; description: string; descriptionAr: string | null; sku: string | null; barcode: string | null; isActive: boolean; defaultUnitPrice: number | null; currency: string | null; unitId: number | null; unit: string | null; unitSet: boolean; unitFromLines: string | null; categoryId: number | null; category: string | null; preferredVendor: { id: number; name: string; appCode: string | null } | null; leadTimeDays: number | null; minStockLevel: number | null; maxStockLevel: number | null; reorderPoint: number | null; allowedUnitCategories: string | null; createdAt: string | null; createdBy: string | null; updatedAt: string | null; updatedBy: string | null };
  scope: { restricted: boolean; showMoney: boolean; departmentIds: number[] };
  stats: { requestLines: number; rejectedLines: number; requestedQty: number; approvedQty: number; departments: string[]; poCount: number; poLines: number; openPoCount: number; outstandingQty: number; vendorCount: number; orderedQty: number; receivedQty: number; acceptedQty: number; rejectedQty: number; returnedQty: number; grnCount: number; failedInspections: number; spendAccepted: number | null; orderedValue: number | null; avgPricePaid: number | null; lastPurchase: { poId: string; poHeaderId: number; poDate: string | null; qty: number | null; vendor: string | null; unitPrice: number | null } | null; firstPurchaseDate: string | null; currency: string };
  requests: CatalogCardRequest[]; orders: CatalogCardOrder[]; receipts: CatalogCardReceipt[]; vendors: CatalogCardVendor[]; costCenters: CatalogCardCostCenter[];
  audit: Array<{ at: string | null; who: string | null; what: string; detail: string | null; kind: 'record' | 'price' }>;
}
export const catalogCard = (id: number) => api.get(`/catalog/${id}/card`).then((r) => r.data as CatalogCard);

// Composer ↔ ERP sync (2026-09-09): pushes from the Item Composer desktop app are staged for review here.
export interface ComposerSyncBucketRow { code: string; cls: string | null; type: string | null; descEn: string; descAr: string; department: string | null; note: string | null; bucket: 'added' | 'changed' | 'removed' | 'dupSkipped' | 'shadeConflict'; changes?: string[]; current?: { id: number; description: string; descriptionAr: string | null; isActive: boolean } | null; conflictWith?: string | null; reactivation?: boolean; vendorRefs?: number; /** sha256s, present only when the picture differs (2026-09-22) */ picture?: { from: string | null; to: string | null } }
export interface ComposerSyncSummary { added: number; changed: number; removed: number; dupSkipped: number; shadeConflict: number; pictures?: number; vocab?: { dictValues: number; dictTypes: number; dictRetire: number; shades: number; motifs: number; departments: number; uoms: number } }
export interface ComposerSyncBatch { id: number; direction: 'push' | 'pull'; status: string; enginePin: string | null; dictHash: string | null; fullSnapshot: boolean; itemCount: number; summary: ComposerSyncSummary | null; sourceIp: string | null; receivedAt: string; reviewedBy: string | null; reviewedAt: string | null; report: any | null }
export interface ComposerSyncBatchDetail extends ComposerSyncBatch { rows: ComposerSyncBucketRow[] }
export const composerSyncBatches = () => api.get('/items/sync/batches').then((r) => r.data as ComposerSyncBatch[]);
export const composerSyncBatch = (id: number) => api.get(`/items/sync/batches/${id}`).then((r) => r.data as ComposerSyncBatchDetail);
export const composerSyncApply = (id: number, accept: { acceptAll?: boolean; acceptCodes?: string[]; removeCodes?: string[] }) => api.post(`/items/sync/batches/${id}/apply`, accept).then((r) => r.data as { report: any; batch: ComposerSyncBatch });
export const composerSyncReject = (id: number) => api.post(`/items/sync/batches/${id}/reject`).then((r) => r.data as ComposerSyncBatch);
export const composerSyncStatus = () => api.get('/items/sync/status').then((r) => r.data as { tokenConfigured: boolean; lastPush: string | null; lastPull: string | null; staged: number; itemsWithComposerData: number; enginePin: string });

// Item pictures (2026-09-22): set in the composer, stored in Spaces, read here through short-lived
// presigned URLs. The ERP never uploads or edits one.
export interface CatalogPicture {
  sha256: string;
  mime: string;
  size: number | null;
  /** Presigned GET, valid for 15 minutes. */
  url: string;
  /** Optional: when the composer last set it, if the server reports it. */
  updatedAt?: string | null;
}
/** Only items that currently have a picture come back. Max 1000 ids per call. */
export const catalogPictures = (ids: number[]) =>
  api.get('/catalog/pictures', { params: { ids: ids.join(',') } }).then((r) => r.data as Record<number, CatalogPicture>);
/** Review thumbnails: the server proxies the bytes for either side of the change (`to` = pushed, `from` = what it replaces). */
export const composerSyncStagedPictureUrl = async (batchId: number, code: string, which: 'to' | 'from' = 'to'): Promise<string> => {
  const { data } = await api.get(`/items/sync/batches/${batchId}/pictures/${encodeURIComponent(code)}`, { params: { which }, responseType: 'blob' });
  return URL.createObjectURL(data as Blob);
};

// Master Items sheet import (xlsx from the part-number app). Dry run unless commit.
export interface CatalogMasterImportResult {
  dryRun: boolean;
  fileName: string | null;
  totalRows: number;
  added: Array<{ code: string; description: string }>;
  updated: Array<{ code: string; changes: string[] }>;
  unchanged: number;
  skipped: Array<{ row: number; code: string; reason: string }>;
  warnings: Array<{ row: number; code: string; message: string }>;
  unmappedUnits: Record<string, number>;
  unmappedVendors: Record<string, number>;
  classCategoryMap: Record<string, string | null>;
  vendorPrices?: { rows: number; added: number; updated: number; defaultsChanged: number; vendorsCreated: string[]; unmappedVendorCodes: Record<string, number> };
  altUnits?: { rows: number; applied: number; unmappedUnits: string[] };
}
export const catalogImportMaster = async (file: File, commit: boolean) => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post(`/catalog/import/master${commit ? '?commit=1' : ''}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data as CatalogMasterImportResult;
};

// Catalog shared types (exported for use in components)
export const refineMRLine = async (lineId: number, data: Record<string, any>) => {
  const { data: result } = await api.patch(`/mrs/lines/${lineId}/refine`, data);
  return result;
};

export interface CatalogItem {
  id: number;
  partNumber: string;
  description: string;
  descriptionAr?: string | null;
  defaultUnitPrice: number | null;
  sku: string | null;
  barcode: string | null;
  active: boolean;
  isActive?: boolean; // server field name; `active` kept for older callers
  allowedUnitCategories?: string | null;
  unit: { id: number; name: string } | null;
  category: { id: number; name: string } | null;
  preferredVendor: { id: number; name: string } | null;
  currency: { id: number; code: string } | null;
  // Inventory Phase 1.6: structured part-number segments
  classCode?: string | null;
  itemCode?: string | null;
  colorCode?: string | null;
  colorVariant?: string | null;
  sizeCode?: string | null;
  softnessCode?: string | null;
  supplierCode?: string | null;
}

export interface CatalogPriceHistory {
  id: number;
  changedAt: string;
  oldPrice: number | null;
  newPrice: number | null;
  changedBy: { id: number; displayName: string } | null;
  reason: string | null;
}

export interface CatalogItemDetail extends CatalogItem {
  priceHistory: CatalogPriceHistory[];
}

export interface CatalogSearchResult {
  id: number;
  partNumber: string;
  description: string;
  descriptionAr?: string | null;
  defaultUnitPrice: number | null;
  allowedUnitCategories?: string | null;
  unit: { id: number; name: string } | null;
  category: { id: number; name: string } | null;
  preferredVendor: { id: number; name: string } | null;
  currency: { id: number; code: string } | null;
}

export interface CatalogCreatePayload {
  partNumber: string;
  description: string;
  defaultUnitPrice?: number | null;
  unitId?: number | null;
  categoryId?: number | null;
  preferredVendorId?: number | null;
  currencyId?: number | null;
  sku?: string | null;
  barcode?: string | null;
  allowedUnitCategories?: string | null;
  // Inventory Phase 1.6: structured part-number segments
  classCode?: string | null;
  itemCode?: string | null;
  colorCode?: string | null;
  colorVariant?: string | null;
  sizeCode?: string | null;
  softnessCode?: string | null;
  supplierCode?: string | null;
}

export interface CatalogUpdatePayload extends Partial<CatalogCreatePayload> {
  priceChangeReason?: string;
}

// =====================================================================
// Audit Trail
// =====================================================================

export const getAuditTrail = (params?: Record<string, any>) =>
  api.get('/audit', { params }).then(r => r.data);

export const getEntityHistory = (entityType: string, entityId: string) =>
  api.get(`/audit/entity/${entityType}/${entityId}`).then(r => r.data);

export const getAuditEntityTypes = () =>
  api.get('/audit/entity-types').then(r => r.data);

// =====================================================================
// Vendor Management
// =====================================================================

export const vendorList = (params?: Record<string, any>) =>
  api.get('/vendors', { params }).then(r => r.data);

export interface VendorOverviewRow { id: number; appCode: string | null; nameAr: string | null; supplies: string[]; pricedItems: number; lastPoId: string | null; lastPoDate: string | null; spend12m: number; onTimePct: number | null; onTimeN: number; driftPct: number | null }
export const vendorOverview = () => api.get('/vendors/overview').then((r) => r.data as VendorOverviewRow[]);
export const vendorStatsExtended = () => api.get('/vendors/stats-extended').then((r) => r.data);
export const vendorDuplicates = () => api.get('/vendors/duplicates').then((r) => r.data as Array<{ key: string; vendors: Array<{ id: number; name: string; status: string; pos: number }> }>);
export const vendorPendingQueue = () => api.get('/vendors/pending-queue').then((r) => r.data as Array<{ id: number; name: string; createdAt: string; requestedFromMrId: string | null; mrLines: number; department: string | null; requester: string | null; matches: Array<{ id: number; name: string; appCode: string | null; status: string; score: number }> }>);
export const vendorMetrics = (id: number) => api.get(`/vendors/${id}/metrics`).then((r) => r.data);
export const vendorStats = () =>
  api.get('/vendors/stats').then(r => r.data);

export const vendorFilters = () =>
  api.get('/vendors/filters').then(r => r.data);

export const vendorGet = (id: number) =>
  api.get(`/vendors/${id}`).then(r => r.data);

export const vendorCreate = (data: Record<string, any>) =>
  api.post('/vendors', data).then(r => r.data);

export const vendorUpdate = (id: number, data: Record<string, any>) =>
  api.patch(`/vendors/${id}`, data).then(r => r.data);

export const vendorDeactivate = (id: number) =>
  api.delete(`/vendors/${id}`).then(r => r.data);

export const vendorSimilar = (name: string, excludeId?: number) =>
  api.get('/vendors/similar', { params: { name, excludeId } }).then(r => r.data);

export const vendorMergePreview = (targetId: number, sourceIds: number[]) =>
  api.post('/vendors/merge/preview', { targetId, sourceIds }).then(r => r.data);

export const vendorMerge = (targetId: number, sourceIds: number[]) =>
  api.post('/vendors/merge', { targetId, sourceIds }).then(r => r.data);

export const vendorContacts = (vendorId: number) =>
  api.get(`/vendors/${vendorId}/contacts`).then(r => r.data);

export const vendorContactCreate = (vendorId: number, data: Record<string, any>) =>
  api.post(`/vendors/${vendorId}/contacts`, data).then(r => r.data);

export const vendorContactUpdate = (vendorId: number, contactId: number, data: Record<string, any>) =>
  api.patch(`/vendors/${vendorId}/contacts/${contactId}`, data).then(r => r.data);

export const vendorContactDelete = (vendorId: number, contactId: number) =>
  api.delete(`/vendors/${vendorId}/contacts/${contactId}`).then(r => r.data);

export const vendorApprove = (id: number, updates?: Record<string, any>) =>
  api.post(`/vendors/${id}/approve`, { updates }).then(r => r.data);

export const vendorReject = (id: number, reason: string) =>
  api.post(`/vendors/${id}/reject`, { reason }).then(r => r.data);

// =====================================================================
// Purchase Returns
// =====================================================================

export const listReturns = (params?: Record<string, any>) =>
  api.get('/returns', { params }).then(r => r.data);

export const getReturnStats = () =>
  api.get('/returns/stats').then(r => r.data);

export const getReturnReasonCodes = () =>
  api.get('/returns/reason-codes').then(r => r.data);

export const updateReturn = (id: number, data: Record<string, any>) =>
  api.patch(`/returns/${id}`, data).then(r => r.data);

export const bulkSettleReturns = (data: Record<string, any>) =>
  api.post('/returns/bulk-settle', data).then(r => r.data);

export const getVendorReturns = (vendorId: number) =>
  api.get(`/returns/vendor/${vendorId}`).then(r => r.data);

// =====================================================================
// Samples (2026-09-30) — replaces Client Samples; charged to cost centers
// =====================================================================

export type SampleCategory = 'fabric' | 'embroidery' | 'product_design';
export type SampleAction =
  | 'edit' | 'upload_mood' | 'remove_image' | 'submit' | 'withdraw'
  | 'create_mr' | 'link' | 'start_purchasing' | 'mark_received' | 'upload_purchased'
  | 'designer_approve' | 'designer_reject' | 'ask_presentation' | 'set_appointment' | 'client_decision'
  | 'accept_conditions' | 'deny_conditions' | 'assign_part' | 'assign_code' | 'new_version';

export interface SampleCcNode { id: number; code: string; name: string; typeCode?: string }
export interface SampleImage { key: string; name: string; mime: string; size: number; at?: string; url: string | null }

export interface SampleRow {
  id: number;
  sampleCode: string;
  versionNo: number;
  category: SampleCategory;
  sampleType: 'client' | 'technical';
  size: 'small' | 'presentation';
  title: string;
  description: string;
  specifications: string | null;
  quantityNote: string | null;
  quantityValue: number | null;
  unit: { id: number; name: string } | null;
  unitId: number | null;
  requestNotes: string | null;
  priority: 'urgent' | 'high' | 'normal' | 'low';
  status: string;
  statusLabel: string;
  waitingOn: string;
  daysInStatus: number;
  departmentId: number;
  department: { id: number; code: string; name: string };
  costCenter: SampleCcNode & { isActive: boolean; externalRef: string | null };
  path: SampleCcNode[];
  client: SampleCcNode | null;
  deal: (SampleCcNode & { externalRef: string | null }) | null;
  mrHeader: { id: number; mrId: string; status: string } | null;
  poHeader: { id: number; poId: string; status: string } | null;
  catalogItem: { id: number; partNumber: string; description: string } | null;
  sampleRefCode: string | null;
  vendorName: string | null;
  actualCost: number | null;
  estimatedArrival: string | null;
  procurementNotes: string | null;
  appointmentDate: string | null;
  clientDecision: 'approved' | 'conditional' | 'rejected' | null;
  clientFeedback: string | null;
  imageCount: number;
  versionCount: number;
  createdBy: { id: number; displayName: string };
  createdAt: string;
  updatedAt: string;
}

export interface SampleDetail extends SampleRow {
  parent: { sampleCode: string; versionNo: number; status: string } | null;
  versions: Array<{ sampleCode: string; versionNo: number; status: string }>;
  designerApprovedBy: { id: number; displayName: string } | null;
  designerApprovedAt: string | null;
  clientDecisionBy: { id: number; displayName: string } | null;
  clientDecisionAt: string | null;
  receivedAt: string | null;
  history: Array<{ at: string; by: { id: number; displayName: string }; action: string; note: string | null; from: string | null; to: string; fromLabel: string | null; toLabel: string }>;
  moodImages: SampleImage[];
  purchasedImages: SampleImage[];
  actions: SampleAction[];
  createdMr?: { id: number; mrId: string };
}

export type SampleView = 'waiting' | 'in_progress' | 'decision_due' | 'done' | 'withdrawn' | 'all';

export interface SampleListParams {
  view?: SampleView; q?: string; status?: string; category?: string; sampleType?: string; size?: string;
  departmentId?: number; priority?: string; costCenterId?: number; from?: string; to?: string; page?: number; limit?: number;
}

export interface SampleInput {
  category: SampleCategory; sampleType: 'client' | 'technical'; size: 'small' | 'presentation';
  costCenterId: number; departmentId?: number; title: string; description: string; specifications?: string;
  quantityNote?: string; quantityValue?: number | null; unitId?: number | null; requestNotes?: string; priority: string;
}

export const listSamples = async (params: SampleListParams): Promise<{ rows: SampleRow[]; total: number; page: number; limit: number; viewCounts: Record<SampleView, number> }> =>
  (await api.get('/samples', { params })).data;

export const getSamplesMe = async (): Promise<{ access: string; canCreate: boolean; isProcurement: boolean; seesAll: boolean; departmentIds: number[] }> =>
  (await api.get('/samples/me')).data;

export const getSample = async (code: string): Promise<SampleDetail> => (await api.get(`/samples/${code}`)).data;

export const createSample = async (payload: SampleInput & { send?: boolean }): Promise<SampleDetail> =>
  (await api.post('/samples', payload)).data;

export const updateSample = async (code: string, payload: SampleInput): Promise<SampleDetail> =>
  (await api.put(`/samples/${code}`, payload)).data;

export const uploadSampleImages = async (code: string, kind: 'mood' | 'purchased', files: File[]): Promise<SampleDetail> => {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  return (await api.post(`/samples/${code}/images/${kind}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
};

export const sampleAction = async (code: string, action: SampleAction, body: Record<string, any> = {}): Promise<SampleDetail> =>
  (await api.post(`/samples/${code}/actions/${action}`, body)).data;

// ═══════════════════════════════════════════════════════════════════════════════
// NOTIFICATIONS
// ═══════════════════════════════════════════════════════════════════════════════

export const getNotifications = (params?: { page?: number; limit?: number; unreadOnly?: boolean }) =>
  api.get('/notifications', { params }).then((r) => r.data);

export const getUnreadNotificationCount = () =>
  api.get('/notifications/unread-count').then((r) => r.data.count as number);

export const markNotificationRead = (id: number) =>
  api.patch(`/notifications/${id}/read`).then((r) => r.data);

export const markAllNotificationsRead = () =>
  api.patch('/notifications/read-all').then((r) => r.data);

export const subscribePush = (subscription: PushSubscriptionJSON) =>
  api.post('/notifications/push/subscribe', subscription).then((r) => r.data);

export const unsubscribePush = (endpoint: string) =>
  api.delete('/notifications/push/unsubscribe', { data: { endpoint } }).then((r) => r.data);

export default api;

// ═══════════════════════════════════════════════════════════════════════════════
// Cost allocation (cost centers on MR / PO lines) — 2026-09-05
// ═══════════════════════════════════════════════════════════════════════════════

export interface CostCenterRef {
  id: number;
  code: string;
  name: string;
  typeCode?: string;
  isActive?: boolean;
  parent?: CostCenterRef | null; // ancestors chain (server sends up to 4 levels up)
}

export interface CostCenterRow extends CostCenterRef {
  typeCode: string;
  label: string;
  externalRef?: string | null;
  notes?: string | null;
  sortOrder: number;
  source: string;
  departmentId?: number | null;
  childCount: number;
  usage: number;
  depth?: number;
  type: { code: string; label: string; parentTypeCode: string | null; childRequired: boolean; anyParent?: boolean; sortOrder: number };
  createdAt?: string;
  updatedAt?: string;
}

export interface CostCenterType {
  code: string;
  label: string;
  parentTypeCode: string | null;
  childRequired: boolean;
  anyParent?: boolean;
  isActive: boolean;
  sortOrder: number;
}

/** Root → node chain */
export const costCenterChain = (cc?: CostCenterRef | null): CostCenterRef[] => {
  const out: CostCenterRef[] = [];
  let cur: CostCenterRef | null | undefined = cc;
  while (cur) { out.unshift(cur); cur = cur.parent; }
  return out;
};

export const costCenterLabel = (cc?: CostCenterRef | null): string => costCenterChain(cc).map((n) => n.name).join(' › ');

export const listAllocCostCenters = async (all = false): Promise<CostCenterRow[]> => {
  const { data } = await api.get('/cost-allocation', { params: all ? { all: 1 } : undefined });
  return data;
};

export const listCostCenterTypes = async (): Promise<CostCenterType[]> => {
  const { data } = await api.get('/cost-allocation/types');
  return data;
};

export const getCostCenterConfig = async (): Promise<{ requiredFrom: string | null; requiredNow: boolean }> => {
  const { data } = await api.get('/cost-allocation/config');
  return data;
};

export const setCostCenterConfig = async (requiredFrom: string | null) => {
  const { data } = await api.put('/cost-allocation/config', { requiredFrom });
  return data;
};

export const createAllocCostCenter = async (payload: {
  code: string; name: string; typeCode: string; parentId?: number | null; externalRef?: string | null; notes?: string | null; sortOrder?: number; departmentId?: number | null;
}) => {
  const { data } = await api.post('/cost-allocation', payload);
  return data;
};

export const updateAllocCostCenter = async (id: number, payload: {
  code?: string; name?: string; externalRef?: string | null; notes?: string | null; sortOrder?: number; isActive?: boolean; departmentId?: number | null; cascade?: boolean;
  ifCodeExists?: 'rename_old' | 'take_over';
}) => {
  const { data } = await api.put(`/cost-allocation/${id}`, payload);
  return data;
};

export const upsertCostCenterType = async (payload: Partial<CostCenterType> & { code: string; label: string }) => {
  const { data } = await api.post('/cost-allocation/types', payload);
  return data;
};

export interface CostCenterImportResult {
  dryRun: boolean;
  source: 'id_register' | 'import';
  added: Array<{ code: string; label: string }>;
  updated: Array<{ code: string; label: string; changes: string[] }>;
  unchanged: number;
  errors: Array<{ row: string; message: string }>;
  logId?: number;
}

export const importCostCenters = async (kind: 'id-register' | 'template' | 'status', file: File, commit: boolean): Promise<CostCenterImportResult> => {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post(`/cost-allocation/import/${kind}`, fd, {
    params: commit ? { commit: 1 } : undefined,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const downloadCostCenterStatusSheet = async () => {
  const response = await api.get('/cost-allocation/status.xlsx', { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = `cost-center-status-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const listCostCenterImports = async () => {
  const { data } = await api.get('/cost-allocation/imports');
  return data;
};

export const setLineCostCenter = async (mrLineId: number, costCenterId: number | null) => {
  const { data } = await api.patch(`/cost-allocation/lines/${mrLineId}`, { costCenterId });
  return data;
};

export const bulkSetLineCostCenter = async (mrLineIds: number[], costCenterId: number | null): Promise<Array<{ mrLineId: number; ok: boolean; error?: string }>> => {
  const { data } = await api.post('/cost-allocation/lines/bulk', { mrLineIds, costCenterId });
  return data;
};

export const listUnallocatedLines = async (params?: { departmentIds?: number[]; status?: string; search?: string; fields?: string[]; limit?: number; offset?: number }) => {
  const { data } = await api.get('/cost-allocation/lines/unallocated', {
    params: {
      ...params,
      departmentIds: params?.departmentIds?.length ? params.departmentIds.join(',') : undefined,
      fields: params?.fields?.length ? params.fields.join(',') : undefined,
    },
  });
  return data as { total: number; rows: any[] };
};

export interface GlobalSearchHit { type: string; id: number | string; title: string; subtitle?: string; url?: string; meta?: Record<string, any> }
export interface GlobalSearchGroup { type: string; label: string; items: GlobalSearchHit[] }
export const globalSearch = async (q: string, types?: string[]): Promise<{ groups: GlobalSearchGroup[]; tookMs: number }> => {
  const { data } = await api.get('/search', { params: { q, types: types?.length ? types.join(',') : undefined } });
  return data;
};

export const listSearchFields = async (): Promise<Array<{ key: string; label: string }>> => {
  const { data } = await api.get('/cost-allocation/lines/search-fields');
  return data;
};

export interface CostAllocationReportParams {
  from?: string; to?: string; costCenterId?: number; departmentId?: number; departmentIds?: number[]; unallocatedOnly?: boolean; includeUnordered?: boolean; stages?: string[];
}

const reportParams = (p?: CostAllocationReportParams) => ({
  from: p?.from || undefined,
  to: p?.to || undefined,
  costCenterId: p?.costCenterId || undefined,
  departmentId: p?.departmentId || undefined,
  departmentIds: p?.departmentIds?.length ? p.departmentIds.join(',') : undefined,
  unallocatedOnly: p?.unallocatedOnly ? 1 : undefined,
  includeUnordered: p?.includeUnordered ? 1 : undefined,
  stages: p?.stages?.length ? p.stages.join(',') : undefined,
});

export const getCostAllocationReport = async (params?: CostAllocationReportParams) => {
  const { data } = await api.get('/cost-allocation/report', { params: reportParams(params) });
  return data as { rows: any[]; summary: any[]; byCategory: any[]; totals: { lines: number; cost: number; unallocated: number } };
};

export const downloadCostAllocationReport = async (params?: CostAllocationReportParams) => {
  const response = await api.get('/cost-allocation/report.xlsx', { params: reportParams(params), responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = `cost-allocation-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// ── Rejected goods step 4c (2026-10-26, mockup DyrAbPJDgtF8t6mjFfytkv "Vendor credit" 1–5): M3 vendor credit ─────────────
export type CreditState = 'ok' | 'near' | 'over' | 'no_cap';
export interface VendorCreditRow {
  vendor: { id: number; name: string; appCode: string | null }; owe: number; cap: number | null; usedPct: number | null; state: CreditState;
  oldestUnpaid: { on: string; days: number; ref: string } | null; theyOweUs: number; lastPaid: { on: string; amount: number; code: string } | null;
}
export interface VendorCreditList {
  warnAt: number;
  tiles: {
    weOwe: { value: number; vendors: number; oldest: { vendor: string; days: number } | null };
    nearOrOver: { count: number; vendors: Array<{ id: number; name: string; usedPct: number | null; state: CreditState }> };
    theyOweUs: { value: number }; paidThisMonth: { value: number; count: number };
  };
  rows: VendorCreditRow[];
}
export interface VendorCreditBrief { vendor: { id: number; name: string }; owe: number; cap: number | null; warnAt: number; usedPct: number | null; state: CreditState }
export interface VendorCreditAccount extends VendorCreditBrief {
  vendor: { id: number; name: string; nameAr?: string | null; appCode?: string | null } & { id: number; name: string };
  capSet: { at: string; by: string | null } | null;
  theyOweUs: { value: number; cases: Array<{ key: string; kind: string; code: string | null; owed: number; label: string }> };
  unpaid: Array<{ id: number; code: string; on: string; days: number; pos: string[]; amount: number; covered: number; outstanding: number }>;
  moves: Array<{ date: string; kind: 'purchase' | 'finance'; what: string; ref: string; refs: string[]; onCredit: number; paid: number; id: number; balance: number; covers?: string[]; proofCount?: number; by?: string | null;
    reversed?: { amount: number; on: string | null; by: string | null; reason: string | null } | null }>; // step 4e: a reversed FIN- payment stays, not counted
}
const VC = '/vendor-credit';
export const getVendorCreditList = async () => (await api.get(VC)).data as VendorCreditList;
export const getVendorCreditBrief = async (vendorId: number) => (await api.get(`${VC}/${vendorId}/brief`)).data as VendorCreditBrief;
export const getVendorCreditAccount = async (vendorId: number) => (await api.get(`${VC}/${vendorId}`)).data as VendorCreditAccount;
export const setVendorCreditCap = async (vendorId: number, cap: number | null, note?: string) => (await api.put(`${VC}/${vendorId}/cap`, { cap, note })).data as VendorCreditBrief;
export const payVendorCredit = async (vendorId: number, body: { paidFrom: 'company_bank' | 'safe'; account?: string; paidOn: string; amount: number; reference?: string; proofFiles?: ProofFile[]; note?: string }) =>
  (await api.post(`${VC}/${vendorId}/payments`, body)).data as { id: number; code: string; covered: Array<{ code: string; amount: number }>; balanceAfter: number };
export const getFinancePaymentProofLink = async (id: number, index: number) => (await api.get(`${VC}/payments/${id}/proof/${index}`)).data.url as string;
// Step 4e: reverse a wrong finance payment (Ahmed or finance), with the reason.
export const reverseFinancePayment = async (id: number, reason: string) =>
  (await api.post(`${VC}/payments/${id}/reverse`, { reason })).data as { id: number; code: string; reversed: boolean; released: Array<{ code: string; amount: number }>; balanceAfter: number };

// ── Rejected goods step 4d (2026-10-26, mockup DyrAbPJDgtF8t6mjFfytkv "Invoice reading" 1–5): M4 invoices ─────────────────
export type InvoiceLineKind = 'item' | 'charge' | 'discount';
export interface InvoiceLine { description: string; qty: number | null; unitPrice: number | null; amount: number | null; kind: InvoiceLineKind; poLineId: number | null; poHeaderId?: number | null; id?: number } // poHeaderId: the PO a charge or discount sits on (step 4g, N3)
export interface InvoiceView {
  id: number; poId: string; source: 'receipt' | 'payment' | 'po'; status: 'read' | 'not_invoice' | 'failed' | 'confirmed' | 'discarded' | 'page_added';
  page?: { invoiceId: number; number: string | null; date: string | null; total: number; pageNo: number } | null; // step 5d (item 13): a page of a confirmed invoice
  repeats?: string | null; // read, and it repeats an invoice already confirmed on this vendor's POs (Ahmed 1 Nov, N5)
  repeatsOn?: string | null; // where that invoice is: "on PO-1" or "shared by PO-1 and PO-2"
  vendor?: string | null;
  repeatOf?: { id: number; number: string | null; samePhoto: boolean; left: Array<{ description: string; amount: number }>; date?: string | null; total?: number; by?: string | null } | null; // the stop (A1, A5): that invoice, whether this is the very same photo, the lines it has on no PO
  parts: Array<{ poHeaderId: number; poId: string; amount: number }>; // a confirmed invoice: how its total splits over its POs (step 4g, N2)
  fileName: string | null; fileMime: string | null; error: string | null; readAt: string | null; model: string | null; seconds: number | null;
  read: { kind: string; vendorName: string | null; vendorMatches: boolean | null; number: string | null; date: string | null; total: number | null; lines: InvoiceLine[]; notes: string | null } | null;
  confirmed: { number: string | null; date: string | null; vendorName: string | null; total: number; typedByHand: boolean; at: string; by: string | null; lines: InvoiceLine[] } | null;
  pos: Array<{ id: number; poId: string }>; // what it can be matched against (N1): the PO it was added on, the POs on the payment, this vendor's others
  poLines: Array<{ id: number; poHeaderId: number; poId: string; lineNo: number; item: string; unit: string | null; qty: number; price: number }>;
}
export interface InvoiceDiff {
  id: number; invoiceId: number; kind: 'price' | 'qty' | 'charge' | 'unmatched' | 'discount' | 'total'; lineNo: number | null; item: string | null; unit: string | null;
  poValue: number | null; invoiceValue: number | null; amount: number; reason: string | null; reasonLabel: string | null; note: string | null;
  explainedBy: string | null; explainedAt: string | null; seen: boolean; reasons: string[];
  vendorOwesUs?: { owed: number; settled: boolean } | null; // step 4e: the vendor's mistake on a line it was paid more for
}
/** A photo on a PO, and how far it got (Ahmed 1 Nov). `reading` may be on another PO: the same photo can be on several. */
export interface PoInvoicePhoto {
  file: ProofFile; from: 'receipt' | 'payment' | 'invoice'; ref: string | null; grnId: string | null; paymentId: number | null; index: number; first: string[];
  reading: { id: number; status: InvoiceView['status']; poId: string; number: string | null; note?: string | null } | null;
}
/** The "Add this PO to the invoice" window (mockup EfVWkyDgKfAePx556MQVCT screen 4). */
export interface AddPoView {
  invoice: { id: number; number: string | null; date: string | null; total: number; by: string | null; fileName: string | null; fileMime: string | null };
  po: { id: number; poId: string; vendor: string | null };
  poLines: Array<{ id: number; lineNo: number; item: string; unit: string | null; qty: number; price: number }>;
  lines: Array<{ id: number; description: string; kind: InvoiceLineKind; qty: number | null; unitPrice: number | null; amount: number; from: number; onPoId: string | null; onLineNo: number | null; poPrice: number | null; movable: boolean; startOn: number | null; waiting: boolean }>;
  parts: Array<{ poHeaderId: number; poId: string; amount: number }>;
}
export type MoveTargets = { pos: Array<{ id: number; poId: string; lines: AddPoView['poLines'] }> };
export interface PoInvoiceCheck {
  poId: string; vendor: string | null; invoices: InvoiceView[]; photos: PoInvoicePhoto[];
  lines: Array<{ id: number; lineNo: number; item: string; code: string | null; unit: string | null; ordered: number; received: number; invoiced: number | null; poPrice: number; invoicePrice: number | null; paid: number; qtyDiffers: boolean; priceDiffers: boolean; qtyNote: string | null }>;
  charges: Array<{ id: number; kind: string; description: string | null; amount: number }>;
  differences: InvoiceDiff[];
  totals: { poValue: number; received: number; invoiced: number | null; paid: number; charges: number; vendorOwes: number; toPay: number };
  openCount: number; openAmount: number;
}
export interface InvoicesToCheck {
  tiles: { toExplain: { count: number; amount: number }; paidNoInvoice: { count: number; oldestDays: number }; unreadable: { count: number }; cleanThisMonth: { count: number } };
  rows: Array<{ po: { id: number; poId: string; status: string }; vendor: { id: number; name: string } | null; invoice: string | null; kind: 'explain' | 'confirm' | 'unreadable' | 'no_invoice' | 'disagree' | 'repeat'; open: string; amount: number | null; action: string; since: string | null; days?: number; flagId?: number }>;
}
export interface PriceDifferences {
  count: number;
  rows: Array<{ id: number; kind: string; poId: string | null; lineNo: number | null; vendor: string | null; item: string; poValue: number | null; invoiceValue: number | null; amount: number; reason: string | null; reasonLabel: string | null; note: string | null; explainedBy: string | null; at: string }>;
}
const IVA = '/invoices';
export const readInvoicePhoto = async (poId: string | number, file: ProofFile, source: 'receipt' | 'payment' | 'po', first?: string[], pageOf?: number) => (await api.post(`${IVA}/read`, { poId, file, source, first, pageOf }, { timeout: 120000 })).data as InvoiceView;
export const readInvoiceAgain = async (id: number, first?: string[]) => (await api.post(`${IVA}/${id}/read`, { first }, { timeout: 120000 })).data as InvoiceView;
export const getInvoice = async (id: number, first?: string[]) => (await api.get(`${IVA}/${id}`, { params: first?.length ? { first: first.join(',') } : undefined })).data as InvoiceView;
export const getInvoicePhotoLink = async (id: number) => (await api.get(`${IVA}/${id}/photo`)).data.url as string;
export const confirmInvoice = async (id: number, body: { differentPaper?: boolean; number?: string | null; date: string; vendorName?: string | null; total: number; lines: Array<{ poLineId: number | null; poHeaderId?: number | null; description: string; qty: number | null; unitPrice: number | null; amount?: number | null; kind: InvoiceLineKind }> }) =>
  (await api.post(`${IVA}/${id}/confirm`, body)).data as InvoiceView;
export const discardInvoice = async (id: number, note?: string) => (await api.post(`${IVA}/${id}/discard`, { note })).data;
// Step 5d (item 9): a PO flagged for a repeated invoice number.
export interface PoFlag { id: number; kind: string; at: string; by: string | null; seen: { at: string; by: string | null } | null; number: string | null; invoiceDate: string | null; invoiceTotal: number | null; other: { poId: string; date: string | null; total: number } | null }
export const getPoFlags = async (poId: string) => (await api.get(`${IVA}/flags/po/${poId}`)).data as PoFlag[];
export const markFlagSeen = async (id: number) => (await api.post(`${IVA}/flags/${id}/seen`)).data;
export const getPoInvoiceCheck = async (poId: string | number) => (await api.get(`${IVA}/po/${poId}`)).data as PoInvoiceCheck;
export const explainInvoiceDifference = async (id: number, reason: string, note?: string, poLineId?: number) => (await api.post(`${IVA}/differences/${id}/explain`, { reason, note, poLineId })).data;
export const getMoveTargets = async (id: number) => (await api.get(`${IVA}/differences/${id}/targets`)).data as MoveTargets;
export const getAddPoView = async (id: number, poId: string, photoId?: number) => (await api.get(`${IVA}/${id}/add-po`, { params: { po: poId, photo: photoId } })).data as AddPoView;
export const addPoToInvoice = async (id: number, body: { poId: string; lines: Array<{ invoiceLineId: number; poLineId: number }>; photoId?: number }) => (await api.post(`${IVA}/${id}/add-po`, body)).data as InvoiceView;
export const markInvoiceDifferenceSeen = async (id: number) => (await api.post(`${IVA}/differences/${id}/seen`)).data;
export const getInvoicesToCheck = async () => (await api.get(`${IVA}/to-check`)).data as InvoicesToCheck;
export const getPriceDifferences = async () => (await api.get(`${IVA}/differences`)).data as PriceDifferences;
export const INVOICE_REASON_LABEL: Record<string, string> = { price_up: 'The price went up', vendor_mistake: "The vendor's mistake", charge: 'A delivery or other charge', agreed_discount: 'An agreed discount', other: 'Something else', moved: 'It is on another PO' };
